import { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';

import { useProfile, useUpdateProfile } from '../../api/hooks/profile.hook';

const IndividualContextSection: React.FC<{
  data: Record<string, unknown>;
  onChange: (data: Record<string, unknown>) => void;
}> = ({ data, onChange }) => {
  const [role, setRole] = useState<string>((data.role as string) || '');
  const [currentWork, setCurrentWork] = useState<string>((data.current_work as string) || '');
  const [workingStyle, setWorkingStyle] = useState<string>((data.working_style as string) || '');
  const [tools, setTools] = useState<string>((data.tools as string) || '');
  const [responsibilities, setResponsibilities] = useState<string>(
    (data.responsibilities as string) || '',
  );

  // Sync local state with prop data when it changes externally
  useEffect(() => {
    setRole((data.role as string) || '');
    setCurrentWork((data.current_work as string) || '');
    setWorkingStyle((data.working_style as string) || '');
    setTools((data.tools as string) || '');
    setResponsibilities((data.responsibilities as string) || '');
  }, [data]);

  const emit = (next: Partial<Record<string, string>>) => {
    const payload = {
      role: next.role ?? role,
      current_work: next.current_work ?? currentWork,
      working_style: next.working_style ?? workingStyle,
      tools: next.tools ?? tools,
      responsibilities: next.responsibilities ?? responsibilities,
    } as Record<string, string>;
    // Don't filter or trim during editing - preserve all input including spaces and line breaks
    onChange(payload);
  };

  return (
    <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6 space-y-6">
      <header>
        <h2 className="text-xl font-semibold text-neutral-900">Individual Context</h2>
        <p className="text-sm text-neutral-600">
          Tell us about your role, current work, and how you prefer to work. This helps the AI
          understand your individual context and working style.
        </p>
      </header>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Role / Job Title
          </label>
          <input
            type="text"
            placeholder="e.g., Software Engineer, Product Manager, Designer"
            value={role}
            onChange={(e) => {
              const v = e.target.value;
              setRole(v);
              emit({ role: v });
            }}
            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Current Work</label>
          <textarea
            placeholder="What are you currently working on? Describe your projects, tasks, and priorities."
            value={currentWork}
            onChange={(e) => {
              const v = e.target.value;
              setCurrentWork(v);
              emit({ current_work: v });
            }}
            rows={4}
            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Working Style</label>
          <textarea
            placeholder="How do you prefer to work? (e.g., collaborative, independent, async, structured, flexible)"
            value={workingStyle}
            onChange={(e) => {
              const v = e.target.value;
              setWorkingStyle(v);
              emit({ working_style: v });
            }}
            rows={3}
            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Tools & Technologies
          </label>
          <textarea
            placeholder="Key tools, technologies, and platforms you use regularly"
            value={tools}
            onChange={(e) => {
              const v = e.target.value;
              setTools(v);
              emit({ tools: v });
            }}
            rows={3}
            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Responsibilities
          </label>
          <textarea
            placeholder="Main responsibilities and areas of focus in your role"
            value={responsibilities}
            onChange={(e) => {
              const v = e.target.value;
              setResponsibilities(v);
              emit({ responsibilities: v });
            }}
            rows={3}
            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
          />
        </div>
      </div>
    </section>
  );
};

const OrganizationalContextSection: React.FC<{
  data: Record<string, unknown>;
  onChange: (data: Record<string, unknown>) => void;
}> = ({ data, onChange }) => {
  const [companyName, setCompanyName] = useState<string>((data.company_name as string) || '');
  const [companyType, setCompanyType] = useState<string>((data.company_type as string) || '');
  const [companySize, setCompanySize] = useState<string>((data.company_size as string) || '');
  const [industry, setIndustry] = useState<string>((data.industry as string) || '');
  const [businessFocus, setBusinessFocus] = useState<string>((data.business_focus as string) || '');
  const [technologies, setTechnologies] = useState<string>((data.technologies as string) || '');
  const [description, setDescription] = useState<string>((data.description as string) || '');

  // Sync local state with prop data when it changes externally
  useEffect(() => {
    setCompanyName((data.company_name as string) || '');
    setCompanyType((data.company_type as string) || '');
    setCompanySize((data.company_size as string) || '');
    setIndustry((data.industry as string) || '');
    setBusinessFocus((data.business_focus as string) || '');
    setTechnologies((data.technologies as string) || '');
    setDescription((data.description as string) || '');
  }, [data]);

  const emitOrg = (next: Partial<Record<string, string>>) => {
    const payload = {
      company_name: next.company_name ?? companyName,
      company_type: next.company_type ?? companyType,
      company_size: next.company_size ?? companySize,
      industry: next.industry ?? industry,
      business_focus: next.business_focus ?? businessFocus,
      technologies: next.technologies ?? technologies,
      description: next.description ?? description,
    } as Record<string, string>;
    // Don't filter or trim during editing - preserve all input including spaces and line breaks
    onChange(payload);
  };

  return (
    <section className="bg-white rounded-xl shadow-sm border border-neutral-200 p-6 space-y-6">
      <header>
        <h2 className="text-xl font-semibold text-neutral-900">Organizational Context</h2>
        <p className="text-sm text-neutral-600">
          Provide information about your organization to help the AI understand your company's
          context, industry, and business focus.
        </p>
      </header>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Company Name</label>
          <input
            type="text"
            placeholder="Your organization's name"
            value={companyName}
            onChange={(e) => {
              const v = e.target.value;
              setCompanyName(v);
              emitOrg({ company_name: v });
            }}
            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Company Type</label>
            <select
              value={companyType}
              onChange={(e) => {
                const v = e.target.value;
                setCompanyType(v);
                emitOrg({ company_type: v });
              }}
              className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="">Select type</option>
              <option value="startup">Startup</option>
              <option value="enterprise">Enterprise</option>
              <option value="agency">Agency</option>
              <option value="nonprofit">Nonprofit</option>
              <option value="consulting">Consulting</option>
              <option value="government">Government</option>
              <option value="education">Education</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Company Size</label>
            <select
              value={companySize}
              onChange={(e) => {
                const v = e.target.value;
                setCompanySize(v);
                emitOrg({ company_size: v });
              }}
              className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="">Select size</option>
              <option value="1-10">1-10 employees</option>
              <option value="11-50">11-50 employees</option>
              <option value="51-200">51-200 employees</option>
              <option value="201-1000">201-1000 employees</option>
              <option value="1000+">1000+ employees</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Industry</label>
          <input
            type="text"
            placeholder="e.g., Technology, Healthcare, Finance, Education, Manufacturing"
            value={industry}
            onChange={(e) => {
              const v = e.target.value;
              setIndustry(v);
              emitOrg({ industry: v });
            }}
            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Business Focus</label>
          <textarea
            placeholder="Primary business/product focus and value proposition"
            value={businessFocus}
            onChange={(e) => {
              const v = e.target.value;
              setBusinessFocus(v);
              emitOrg({ business_focus: v });
            }}
            rows={3}
            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">Technologies</label>
          <textarea
            placeholder="Key technologies, platforms, and tools used by your organization"
            value={technologies}
            onChange={(e) => {
              const v = e.target.value;
              setTechnologies(v);
              emitOrg({ technologies: v });
            }}
            rows={3}
            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-1">
            Company Description
          </label>
          <textarea
            placeholder="Brief description of your company and its mission"
            value={description}
            onChange={(e) => {
              const v = e.target.value;
              setDescription(v);
              emitOrg({ description: v });
            }}
            rows={3}
            className="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
          />
        </div>
      </div>
    </section>
  );
};

const ProfilePage: React.FC = () => {
  const { data: profileData, isLoading } = useProfile();
  const updateProfile = useUpdateProfile();
  const [individualContext, setIndividualContext] = useState<Record<string, unknown>>({});
  const [organizationalContext, setOrganizationalContext] = useState<Record<string, unknown>>({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (profileData) {
      setIndividualContext(profileData.individual_context || {});
      setOrganizationalContext(profileData.organizational_context || {});
      setHasChanges(false);
    }
  }, [profileData]);

  const handleSave = async () => {
    try {
      // Filter out empty strings - only send fields with actual values
      // This prevents empty fields from overwriting existing data
      // Preserve all user input exactly as typed (including spaces and line breaks)
      const filterEmptyStrings = (obj: Record<string, unknown>): Record<string, unknown> => {
        const filtered: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(obj)) {
          // Only include non-empty strings (check if trimmed version is empty to filter out whitespace-only strings)
          // But preserve the original value without trimming
          if (typeof value === 'string' && value.trim() !== '') {
            filtered[key] = value; // Preserve original value, don't trim
          } else if (value !== '' && value !== null && value !== undefined) {
            // Include non-string values that aren't empty
            filtered[key] = value;
          }
        }
        return filtered;
      };

      const filteredIndividual = filterEmptyStrings(individualContext);
      const filteredOrganizational = filterEmptyStrings(organizationalContext);

      await updateProfile.mutateAsync({
        individual_context:
          Object.keys(filteredIndividual).length > 0 ? filteredIndividual : undefined,
        organizational_context:
          Object.keys(filteredOrganizational).length > 0 ? filteredOrganizational : undefined,
      });
      toast.success('Profile updated successfully');
      setHasChanges(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to update profile.';
      toast.error(message);
    }
  };

  const handleIndividualChange = (data: Record<string, unknown>) => {
    setIndividualContext(data);
    setHasChanges(true);
  };

  const handleOrganizationalChange = (data: Record<string, unknown>) => {
    setOrganizationalContext(data);
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-neutral-500">Loading profile…</div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-neutral-900">Profile Settings</h1>
          <p className="text-sm text-neutral-600">
            Manage your individual and organizational context to help the AI better understand your
            work and organization.
          </p>
        </div>

        <IndividualContextSection data={individualContext} onChange={handleIndividualChange} />
        <OrganizationalContextSection
          data={organizationalContext}
          onChange={handleOrganizationalChange}
        />

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges || updateProfile.isLoading}
            className="inline-flex items-center rounded-md bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateProfile.isLoading ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
